import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { ProgramPermissions } from 'types/store/normalized';

/* ============================== STATE SETUP =============================== */
interface ProgramPermissionsState {
  status: Status;
  loadingProgramIds: string[];
  notFoundProgramIds: string[];
}

const initialState: ProgramPermissionsState = {
  status: SLICE_STATUS.IDLE,
  loadingProgramIds: [],
  notFoundProgramIds: [],
};

const adapter = createEntityAdapter<ProgramPermissions>();

const selectProgramPermissionsState = (state: RootState) =>
  state.normalized.programPermissions;

/* ============================== HELPERS =================================== */
interface GetListParams {
  programIds?: string[];
  pageSize?: number;
}

const NO_OP_RESPONSE: ProgramPermissions[] = [];

function getNewProgramIdsToFetch(
  state: RootState,
  programIds: string[]
): string[] {
  const { entities, notFoundProgramIds = [] } =
    selectProgramPermissionsState(state);

  const notFoundSet = new Set(notFoundProgramIds);

  return programIds.filter((id) => {
    const isCached = Boolean(entities[id]);
    const isNotFound = notFoundSet.has(id);
    return !isCached && !isNotFound;
  });
}

/* ============================== REDUX THUNK =============================== */
function concurrencyCondition(): boolean {
  return true;
}

export const getProgramPermissions = createAsyncThunk<
  ProgramPermissions[],
  GetListParams,
  ThunkApi
>(
  'normalized/programPermissions/getProgramPermissions',
  async (
    { programIds = [], pageSize = 100 },
    { getState, extra: { getProgramPermissionsApi } }
  ) => {
    const state = getState();

    const cleanedIds = programIds.filter(
      (id): id is string => typeof id === 'string' && id.trim() !== ''
    );

    const idsToFetch = getNewProgramIdsToFetch(state, cleanedIds);
    if (idsToFetch.length === 0) {
      return NO_OP_RESPONSE;
    }

    const { data } = await getProgramPermissionsApi().getByIdsPost({
      programIds: idsToFetch,
    });

    return data;
  },
  { condition: concurrencyCondition }
);

/* ================================= REDUCER ================================ */
const programPermissionsSlice = createSlice({
  name: 'normalized/programPermissions',
  initialState: adapter.getInitialState(initialState),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProgramPermissions.pending, (state, action) => {
        const { programIds = [] } = action.meta.arg;
        state.status = SLICE_STATUS.LOADING;

        const current = new Set(state.loadingProgramIds);
        programIds.forEach((id) => current.add(id));
        state.loadingProgramIds = Array.from(current);
      })
      .addCase(getProgramPermissions.rejected, (state, action) => {
        const { programIds = [] } = action.meta.arg;

        state.status = SLICE_STATUS.FAILED;

        state.loadingProgramIds = state.loadingProgramIds.filter(
          (id) => !programIds.includes(id)
        );
      })
      .addCase(getProgramPermissions.fulfilled, (state, action) => {
        const { programIds = [] } = action.meta.arg;

        state.loadingProgramIds = state.loadingProgramIds.filter(
          (id) => !programIds.includes(id)
        );

        adapter.upsertMany(state, action.payload);
        state.status = SLICE_STATUS.SUCCESS;

        if (programIds.length > 0) {
          const returnedIds = action.payload.map((p) => p.id);
          const requestedNotFound = programIds.filter(
            (id) => !returnedIds.includes(id)
          );

          if (requestedNotFound.length > 0) {
            const current = new Set(state.notFoundProgramIds);
            requestedNotFound.forEach((id) => current.add(id));
            state.notFoundProgramIds = Array.from(current);
          }
        }
      });
  },
});

/* =============================== SELECTORS ================================ */
const defaultSelectors = adapter.getSelectors<RootState>(
  (state) => state.normalized.programPermissions
);

const selectStatus = (state: RootState) =>
  state.normalized.programPermissions.status;

const selectProgramPermissionsById = (state: RootState) => (id: string) =>
  defaultSelectors.selectById(state, id);

export const programPermissionsSelectors = {
  ...defaultSelectors,
  selectStatus,
  selectProgramPermissionsById,
};

export default programPermissionsSlice.reducer;
